import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  IconButton,
  FormControl,
  MenuItem,
  Pagination,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Chip,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  FilterListOff as FilterListOffIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { lessonsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import Select from '../components/UIKit/Select';
import DeleteLessonDialog from '../components/DeleteLessonDialog';
import { LessonCard } from '../components';
import { useTranslation } from 'react-i18next';
import SearchInput from '../components/UIKit/SearchInput';
import { usersAPI } from '../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';
import { fetchResponsibilities, selectAllResponsibilities, selectResponsibilitiesLoading } from '../redux/slices/responsibilitiesSlice';
import { fetchTools, selectAllTools, selectToolsLoading } from '../redux/slices/toolsSlice';
import TextField from '../components/UIKit/TextField';

const LessonLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['lessons', 'common', 'tests']);
  const dispatch = useDispatch();
  
  // Используем данные из Redux вместо локального состояния
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);
  const responsibilities = useSelector(selectAllResponsibilities);
  const responsibilitiesLoading = useSelector(selectResponsibilitiesLoading);
  const tools = useSelector(selectAllTools);
  const toolsLoading = useSelector(selectToolsLoading);

  // Создаём справочники для быстрого поиска по ID
  const skillsMap = React.useMemo(() => {
    return skills.reduce((acc, skill) => {
      acc[skill.id] = skill;
      return acc;
    }, {});
  }, [skills]);

  const professionsMap = React.useMemo(() => {
    return professions.reduce((acc, profession) => {
      acc[profession.id] = profession;
      return acc;
    }, {});
  }, [professions]);
  
    // Helper function to get the correct content type translation
  const getContentTypeLabel = (contentType) => {
    const validTypes = [
      'standard',
      'labyrinth',
      'flippingCards',
      'mixed',
      'memoryGame',
      'tagCloud',
      'rolePlayGame',
      'textReconstruction',
      'presentation',
      'fullHtml',
      'htmlBlock',
      'video',
    ];
    
    if (!validTypes.includes(contentType)) {
      return t('lessons:contentTypes.other');
    }
    if (contentType === 'mixed') {
      return t(`lessons:contentTypes.${contentType}.name`);
    }
    return t(`lessons:contentTypes.${contentType}.name`) || t(`lessons:contentTypes.${contentType}`);
  };

  // Helper function to get correct plural form for count
  const getPluralForm = (count, type) => {
    if (count === 1) {
      return t(`lessons:library.${type}.one`);
    } else if (count >= 2 && count <= 4) {
      return t(`lessons:library.${type}.few`);
    } else {
      return t(`lessons:library.${type}.many`);
    }
  };

  // Состояние для хранения списка уроков
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Состояние для пагинации
  const [page, setPage] = useState(1);
  const limit = 9;
  const [total, setTotal] = useState(0);

  // Состояние для фильтров
  const [filters, setFilters] = useState({
    search: '',
    instructor: '',
    isDraft: '',
    updatedViaApi: '',
    type: '',
    contentType: '',
    professions: [],
    skills: [],
    responsibilities: [],
    tools: [],
  });

  // Состояние для диалога удаления урока
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [instructors, setInstructors] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [testMenuAnchor, setTestMenuAnchor] = useState(null);
  const [testMenuItems, setTestMenuItems] = useState([]);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    dispatch(fetchSkills({ all: true, isCommon: 1 }));
    dispatch(fetchResponsibilities({ all: true, isCommon: 1 }));
    dispatch(fetchTools({ all: true, isCommon: 1 }));
  }, [dispatch]);

  // Функция для загрузки уроков с сервера
  const fetchLessons = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...filters,
      };

      // Если выбраны профессии
      if (filters.professions && filters.professions.length > 0) {
        params.professions = filters.professions
          .map((prof) =>
            typeof prof === 'object' ? prof.id || prof._id : prof
          )
          .join(',');
      }

      // Если выбраны навыки
      if (filters.skills && filters.skills.length > 0) {
        params.skills = filters.skills
          .map((skill) =>
            typeof skill === 'object' ? skill.id || skill._id : skill
          )
          .join(',');
      }

      // Если выбраны ответственности
      if (filters.responsibilities && filters.responsibilities.length > 0) {
        params.responsibilities = filters.responsibilities
          .map((resp) =>
            typeof resp === 'object' ? resp.id || resp._id : resp
          )
          .join(',');
      }

      // Если выбраны инструменты
      if (filters.tools && filters.tools.length > 0) {
        params.tools = filters.tools
          .map((tool) =>
            typeof tool === 'object' ? tool.id || tool._id : tool
          )
          .join(',');
      }

      // Удаляем пустые параметры
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await lessonsAPI.getAll(params);

      if (response && response.data && response.data.success) {
        setLessons(response.data.data);
        setTotal(response.data.pagination.total);
        setError(null); // Очищаем ошибку при успешном запросе
      } else {
        setError(t('lessons:errors.loadError'));
      }
    } catch (err) {
      console.error('Error fetching lessons:', err);
      setError(t('lessons:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, t]);

  // Загрузка уроков при монтировании компонента и изменении фильтров или страницы
  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  useEffect(() => {
    // Fetch instructors for the filter only if user is authenticated
    if (!user) return;
    async function fetchInstructors() {
      try {
        const res = await usersAPI.getAll({ all: true });
        setInstructors(res.data.data.users || []);
      } catch (e) {
        setInstructors([]);
      }
    }
    fetchInstructors();
  }, [user]);

  // Функция для изменения страницы
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Функция для изменения фильтров
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Сбрасываем страницу при изменении фильтров
  };

  // Функция для сброса фильтров
  const handleResetFilters = () => {
    setFilters({
      search: '',
      instructor: '',
      isDraft: '',
      updatedViaApi: '',
      type: '',
      contentType: '',
      professions: [],
      skills: [],
      responsibilities: [],
      tools: [],
    });
    setPage(1);
  };

  // Обработчик изменения профессий
  const handleProfessionsChange = (event, newValue) => {
    setFilters({
      ...filters,
      professions: Array.isArray(newValue) ? newValue : [],
    });
    setPage(1);
  };

  // Обработчик удаления профессии из списка выбранных
  const handleDeleteProfession = (indexToDelete) => {
    const newProfessions = [...filters.professions];
    newProfessions.splice(indexToDelete, 1);
    setFilters({
      ...filters,
      professions: newProfessions,
    });
    setPage(1);
  };

  // Обработчик изменения навыков
  const handleSkillsChange = (event, newValue) => {
    setFilters({
      ...filters,
      skills: Array.isArray(newValue) ? newValue : [],
    });
    setPage(1);
  };

  // Обработчик удаления навыка из списка выбранных
  const handleDeleteSkill = (indexToDelete) => {
    const newSkills = [...filters.skills];
    newSkills.splice(indexToDelete, 1);
    setFilters({
      ...filters,
      skills: newSkills,
    });
    setPage(1);
  };

  // Обработчик изменения ответственностей
  const handleResponsibilitiesChange = (event, newValue) => {
    setFilters({
      ...filters,
      responsibilities: Array.isArray(newValue) ? newValue : [],
    });
    setPage(1);
  };

  // Обработчик удаления ответственности из списка выбранных
  const handleDeleteResponsibility = (indexToDelete) => {
    const newResponsibilities = [...filters.responsibilities];
    newResponsibilities.splice(indexToDelete, 1);
    setFilters({
      ...filters,
      responsibilities: newResponsibilities,
    });
    setPage(1);
  };

  // Обработчик изменения инструментов
  const handleToolsChange = (event, newValue) => {
    setFilters({
      ...filters,
      tools: Array.isArray(newValue) ? newValue : [],
    });
    setPage(1);
  };

  // Обработчик удаления инструмента из списка выбранных
  const handleDeleteTool = (indexToDelete) => {
    const newTools = [...filters.tools];
    newTools.splice(indexToDelete, 1);
    setFilters({
      ...filters,
      tools: newTools,
    });
    setPage(1);
  };

  // Функция для создания нового урока
  const handleCreateLesson = () => {
    navigate('/lessons/create');
  };

  // Функция для редактирования урока
  const handleEditLesson = (lessonSlug) => {
    navigate(`/lessons/edit/${lessonSlug}`);
  };

  // Функция для просмотра урока
  const handleViewLesson = (lessonSlug) => {
    navigate(`/lessons/${lessonSlug}`);
  };

  // Функция для открытия диалога подтверждения удаления
  const handleDeleteConfirm = (lesson) => {
    setLessonToDelete(lesson);
    setDeleteDialogOpen(true);
  };

  // Функция для удаления урока
  const handleDeleteLesson = async () => {
    if (!lessonToDelete) return;

    try {
      setDeleting(true);
      await lessonsAPI.delete(lessonToDelete._id);
      fetchLessons(); // Обновляем список после удаления
      setDeleteDialogOpen(false);
      setLessonToDelete(null);
      setDependencies(null);
    } catch (err) {
      console.error('Error deleting lesson:', err);

      // Проверяем, есть ли зависимости
      if (err.response?.data?.code === 'LESSON_HAS_DEPENDENCIES') {
        // Показываем информацию о зависимостях в модальном окне
        const deps = err.response.data.dependencies;
        setDependencies(deps);
        setDeleteDialogOpen(true);
        return;
      }

      // Если урок используется в курсах, показываем информацию о них (старая логика)
      if (err.response && err.response.data && err.response.data.courses) {
        setDependencies({ courses: err.response.data.courses, modules: [] });
        setDeleteDialogOpen(true);
      } else {
        setError(err.response?.data?.error || t('lessons:errors.deleteError'));
        setDeleteDialogOpen(false);
      }
    } finally {
      setDeleting(false);
    }
  };

  // Функция для просмотра курсов, в которых используется урок
  const handleViewCourses = async (lesson) => {
    try {
      const [coursesResponse, modulesResponse] = await Promise.all([
        lessonsAPI.getCourses(lesson._id),
        lessonsAPI.getModules(lesson._id)
      ]);

      const courses = coursesResponse?.data?.data || [];
      const modules = modulesResponse?.data?.data || [];

      setDependencies({ courses, modules });
      setLessonToDelete(lesson);
      setDeleteDialogOpen(true);
    } catch (err) {
      console.error('Error fetching lesson dependencies:', err);
      setError(t('lessons:errors.loadCoursesError'));
    }
  };

  // Проверка прав пользователя
  const isAdmin =
    user && user.userMeta && user.userMeta.roles.includes('admin');
  const isTeacher =
    user && user.userMeta && user.userMeta.roles.includes('teacher');

  // Функция для определения, может ли пользователь редактировать урок
  const canEditLesson = (lesson) => {
    if (!user || !lesson) return false;
    if (isAdmin || isTeacher) return true;
    return lesson.instructor && lesson.instructor._id === user._id;
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters =
    filters.search !== '' ||
    filters.professions.length > 0 ||
    filters.skills.length > 0 ||
    filters.responsibilities.length > 0 ||
    filters.tools.length > 0 ||
    filters.instructor !== '' ||
    filters.isDraft !== '' ||
    filters.updatedViaApi !== '' ||
    filters.type !== '' ||
    filters.contentType !== '';

  // Обработчик переключения отображения расширенных фильтров
  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography component="h1" variant="h3" gutterBottom>
            <MenuBookIcon
              sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }}
            />
            {t('lessons:library.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
            {user && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/lessons/my')}
                sx={{ minWidth: 'auto' }}
              >
                {t('lessons:library.myLessons')}
              </Button>
            )}
            {/* Show create lesson button only for authenticated users */}
            {user && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateLesson}
                sx={{ minWidth: 'auto' }}
              >
                {t('lessons:library.createLesson')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Фильтры и поиск */}
        <Box sx={{ mb: hasActiveFilters ? 0 : 4 }} className="filters">
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}
          >
            <SearchInput
              value={filters.search}
              onChange={val => handleFilterChange({ target: { name: 'search', value: val } })}
              placeholder={t('lessons:library.filters.searchLabel')}
              debounceTime={400}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={toggleAdvancedFilters}
              startIcon={
                showAdvancedFilters ? <FilterListOffIcon /> : <FilterListIcon />
              }
              sx={{
                minWidth: { xs: '100%', sm: '180px' },
                height: { xs: '48px', sm: 'auto' },
                fontWeight: 500,
              }}
            >
              {showAdvancedFilters
                ? t('lessons:library.filters.hideFilters')
                : t('lessons:library.filters.showFilters')}
            </Button>

            {hasActiveFilters && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
                sx={{
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                  minWidth: { xs: '100%', sm: 'auto' },
                  height: { xs: '48px', sm: 'auto' },
                }}
              >
                {t('lessons:library.filters.clearAll')}
              </Button>
            )}
          </Box>

          {/* Расширенные фильтры */}
          {showAdvancedFilters && (
            <Box sx={{ mb: 3 }}>
              {/* Профессии на всю ширину */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  multiple
                  id="professions-select"
                  options={professionsLoading ? [] : professions}
                  getOptionLabel={(option) =>
                    option.library && option.library.name
                      ? option.library.name
                      : option.name || ''
                  }
                  value={filters.professions || []}
                  onChange={handleProfessionsChange}
                  isOptionEqualToValue={(option, value) =>
                    (option.id && value.id && option.id === value.id) ||
                    (option._id && value._id && option._id === value._id)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={
                          option.library && option.library.name
                            ? option.library.name
                            : option.name || ''
                        }
                        {...getTagProps({ index })}
                        key={option.id || index}
                        color="primary"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteProfession(index);
                        }}
                        size="medium"
                        sx={{ m: 0.3 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        filters.professions && filters.professions.length > 0
                          ? ''
                          : professionsLoading
                          ? t('lessons:library.filters.loading')
                          : t('lessons:library.filters.selectProfessions')
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {professionsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={professionsLoading}
                  disableCloseOnSelect
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                      flexWrap: 'wrap',
                    },
                  }}
                />
              </Box>

              {/* Навыки на всю ширину */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  multiple
                  id="skills-select"
                  options={skillsLoading ? [] : skills}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.skills || []}
                  onChange={handleSkillsChange}
                  isOptionEqualToValue={(option, value) =>
                    (option.id && value.id && option.id === value.id) ||
                    (option._id && value._id && option._id === value._id)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || ''}
                        {...getTagProps({ index })}
                        key={option.id || index}
                        color="secondary"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteSkill(index);
                        }}
                        size="medium"
                        sx={{ m: 0.3 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        filters.skills && filters.skills.length > 0
                          ? ''
                          : skillsLoading
                          ? t('lessons:library.filters.loading')
                          : t('lessons:library.filters.selectSkills')
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {skillsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={skillsLoading}
                  disableCloseOnSelect
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                      flexWrap: 'wrap',
                    },
                  }}
                />
              </Box>

              {/* Ответственности на всю ширину */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  multiple
                  id="responsibilities-select"
                  options={responsibilitiesLoading ? [] : responsibilities}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.responsibilities || []}
                  onChange={handleResponsibilitiesChange}
                  isOptionEqualToValue={(option, value) =>
                    (option.id && value.id && option.id === value.id) ||
                    (option._id && value._id && option._id === value._id)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || ''}
                        {...getTagProps({ index })}
                        key={option.id || index}
                        color="info"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteResponsibility(index);
                        }}
                        size="medium"
                        sx={{ m: 0.3 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        filters.responsibilities && filters.responsibilities.length > 0
                          ? ''
                          : responsibilitiesLoading
                          ? t('lessons:library.filters.loading')
                          : t('lessons:library.filters.selectResponsibilities')
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {responsibilitiesLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={responsibilitiesLoading}
                  disableCloseOnSelect
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                      flexWrap: 'wrap',
                    },
                  }}
                />
              </Box>

              {/* Инструменты на всю ширину */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  multiple
                  id="tools-select"
                  options={toolsLoading ? [] : tools}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.tools || []}
                  onChange={handleToolsChange}
                  isOptionEqualToValue={(option, value) =>
                    (option.id && value.id && option.id === value.id) ||
                    (option._id && value._id && option._id === value._id)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || ''}
                        {...getTagProps({ index })}
                        key={option.id || index}
                        color="success"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteTool(index);
                        }}
                        size="medium"
                        sx={{ m: 0.3 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        filters.tools && filters.tools.length > 0
                          ? ''
                          : toolsLoading
                          ? t('lessons:library.filters.loading')
                          : t('lessons:library.filters.selectTools')
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {toolsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={toolsLoading}
                  disableCloseOnSelect
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                      flexWrap: 'wrap',
                    },
                  }}
                />
              </Box>

              {/* Остальные фильтры */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                }}
              >
                {/* Тип урока */}
                <FormControl sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' } }}>
                  <Select
                    name="type"
                    value={filters.type}
                    onChange={handleFilterChange}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return <span style={{ color: 'white' }}>{t('lessons:library.filters.allTypes')}</span>;
                      }
                      return t(`lessons:types.${selected}`);
                    }}
                  >
                    <MenuItem value="">
                      {t('lessons:library.filters.allTypes')}
                    </MenuItem>
                    <MenuItem value="text">{t('lessons:types.text')}</MenuItem>
                    <MenuItem value="video">{t('lessons:types.video')}</MenuItem>
                    <MenuItem value="interactive">
                      {t('lessons:types.interactive')}
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Тип контента */}
                <FormControl sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' } }}>
                  <Select
                    name="contentType"
                    value={filters.contentType}
                    onChange={handleFilterChange}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return <span style={{ color: 'white' }}>{t('lessons:library.filters.allTypes')}</span>;
                      }
                      return getContentTypeLabel(selected);
                    }}
                  >
                    <MenuItem value="">
                      {t('lessons:library.filters.allTypes')}
                    </MenuItem>
                    <MenuItem value="standard">{getContentTypeLabel('standard')}</MenuItem>
                    <MenuItem value="labyrinth">{getContentTypeLabel('labyrinth')}</MenuItem>
                    <MenuItem value="flippingCards">{getContentTypeLabel('flippingCards')}</MenuItem>
                    <MenuItem value="mixed">{getContentTypeLabel('mixed')}</MenuItem>
                    <MenuItem value="memoryGame">{getContentTypeLabel('memoryGame')}</MenuItem>
                    <MenuItem value="tagCloud">{getContentTypeLabel('tagCloud')}</MenuItem>
                    <MenuItem value="rolePlayGame">{getContentTypeLabel('rolePlayGame')}</MenuItem>
                    <MenuItem value="textReconstruction">{getContentTypeLabel('textReconstruction')}</MenuItem>
                    <MenuItem value="presentation">{getContentTypeLabel('presentation')}</MenuItem>
                    <MenuItem value="fullHtml">{getContentTypeLabel('fullHtml')}</MenuItem>
                    <MenuItem value="htmlBlock">{getContentTypeLabel('htmlBlock')}</MenuItem>
                    <MenuItem value="video">{getContentTypeLabel('video')}</MenuItem>
                  </Select>
                </FormControl>

                {/* Инструктор */}
                <FormControl sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' } }}>
                  <Select
                    name="instructor"
                    value={filters.instructor}
                    onChange={handleFilterChange}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return <span style={{ color: 'white' }}>{t('lessons:author')}</span>;
                      }
                      const instructor = instructors?.find(inst => inst.id === selected);
                      return instructor?.name || instructor?.fullName || instructor?.email || selected;
                    }}
                  >
                    <MenuItem value="">{t('common:all')}</MenuItem>
                    {instructors?.map(inst => (
                      <MenuItem key={inst.id} value={inst.id}>
                        {inst.name || inst.fullName || inst.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Статус (только для admin/teacher) */}
                {(isAdmin || isTeacher) && (
                  <FormControl sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' } }}>
                    <Select
                      name="isDraft"
                      value={filters.isDraft}
                      onChange={handleFilterChange}
                      size="small"
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected === '') {
                          return <span style={{ color: 'white' }}>{t('lessons:library.filters.allStatuses')}</span>;
                        }
                        return selected === 'true' ? t('common:status.draft') : t('common:status.published');
                      }}
                    >
                      <MenuItem value="">
                        {t('lessons:library.filters.allStatuses')}
                      </MenuItem>
                      <MenuItem value="true">{t('common:status.draft')}</MenuItem>
                      <MenuItem value="false">{t('common:status.published')}</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Обновлено через API */}
                <FormControl sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)' } }}>
                  <Select
                    name="updatedViaApi"
                    value={filters.updatedViaApi}
                    onChange={handleFilterChange}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return <span style={{ color: 'white' }}>{t('lessons:library.filters.allUpdatedViaApi')}</span>;
                      }
                      return selected === 'true' ? t('common:yes') : t('common:no');
                    }}
                  >
                    <MenuItem value="">{t('lessons:library.filters.allUpdatedViaApi')}</MenuItem>
                    <MenuItem value="true">{t('common:yes')}</MenuItem>
                    <MenuItem value="false">{t('common:no')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
        </Box>

        {/* Список уроков */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', my: 5 }}>
            <Typography color="error">{error}</Typography>
            <Button variant="contained" onClick={fetchLessons} sx={{ mt: 2 }}>
              {t('common:actions.retry')}
            </Button>
          </Box>
        ) : lessons.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {t('lessons:library.noLessons')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {lessons.map((lesson) => (
              <Grid item xs={12} sm={6} md={4} key={lesson._id}>
                <LessonCard
                  lesson={lesson}
                  variant="default"
                  showAuthor={true}
                  showContent={true}
                  showModuleInfo={false}
                  showCourseInfo={false}
                  showViewButton={false}
                  skillsMap={skillsMap}
                  professionsMap={professionsMap}
                  customButtons={
                    <Box sx={{ position: 'relative', width: '100%', mt: 4 }}>
                      {/* Action buttons - BOTTOM LEFT */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Tooltip title={t('common:actions.view')}>
                          <IconButton size="small" onClick={() => handleViewLesson(lesson.slug || lesson._id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {isAdmin && lesson.tests && lesson.tests.length > 0 && (
                          <Tooltip title={`${t('tests:view.viewTest')} (${lesson.tests.length})`}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                if (lesson.tests.length === 1) {
                                  navigate(`/admin/tests/${lesson.tests[0]}`);
                                } else {
                                  setTestMenuItems(lesson.tests);
                                  setTestMenuAnchor(e.currentTarget);
                                }
                              }}
                            >
                              <QuizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canEditLesson(lesson) && (
                          <Tooltip title={t('common:actions.edit')}>
                            <IconButton size="small" onClick={() => handleEditLesson(lesson.slug || lesson._id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canEditLesson(lesson) && (
                          <Tooltip title={t('common:actions.delete')}>
                            <IconButton size="small" onClick={() => handleDeleteConfirm(lesson)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Courses count + Hat icon - BOTTOM RIGHT */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          display: 'flex',
                          alignItems: 'center',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {/* Courses count */}
                        {lesson.coursesCount !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              sx={{ mr: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}
                            >
                              {lesson.coursesCount} {getPluralForm(lesson.coursesCount, 'courseCount')}
                            </Typography>
                            <Tooltip title={t('lessons:library.coursesWithLesson')}>
                              <IconButton size="small" onClick={() => handleViewCourses(lesson)}>
                                <SchoolIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        
                        {/* Modules count */}
                        {lesson.modulesCount !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              sx={{ mr: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}
                            >
                              {lesson.modulesCount} {getPluralForm(lesson.modulesCount, 'moduleCount')}
                            </Typography>
                            <Tooltip title={t('lessons:library.modulesWithLesson')}>
                              <IconButton size="small" disabled>
                                <MenuBookIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                  sx={{
                    opacity: lesson.isDraft ? 0.8 : 1,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Пагинация */}
        {total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Box>

      {/* Меню вибору тесту (коли тестів > 1) */}
      <Menu
        anchorEl={testMenuAnchor}
        open={Boolean(testMenuAnchor)}
        onClose={() => setTestMenuAnchor(null)}
      >
        {testMenuItems.map((testId, idx) => (
          <MenuItem
            key={testId}
            onClick={() => {
              setTestMenuAnchor(null);
              navigate(`/admin/tests/${testId}`);
            }}
          >
            {t('tests:view.testNumber', { number: idx + 1 })}
          </MenuItem>
        ))}
      </Menu>

      {/* Универсальный диалог удаления урока */}
      <DeleteLessonDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setLessonToDelete(null);
          setDependencies(null);
        }}
        onConfirm={handleDeleteLesson}
        lesson={lessonToDelete}
        dependencies={dependencies}
        loading={deleting}
      />
    </Container>
  );
};

export default LessonLibrary;
